import React, { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Divider } from '@mui/material';
import { AuthContext } from '../../context/authContext';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import InfoIcon from '@mui/icons-material/Info';
import { apiPost, showErrorToast } from '../../services/apiClient';
import { buildApiUrl, API_ENDPOINTS } from '../../config/apiEndpoints';
import { toast } from '../../utils/toast';
import { useTranslation } from 'react-i18next';

interface UserMenuProps {
  showUserInfo?: boolean;
}

interface MenuItemButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'logout';
}

// Styles
const styles = {
  menuButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    px: 3,
    py: 2,
    backgroundColor: (theme: any) => theme.palette.background.lightGrey,
    color: 'text.grey',
    borderRadius: '12px',
    border: (theme: any) => `1px solid ${theme.palette.border.light}`,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: (theme: any) => theme.typography.fontSizes.body,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: (theme: any) => theme.palette.mode === 'dark'
        ? theme.palette.background.darkGrey
        : theme.palette.background.inputGrey,
      transform: 'translateY(-1px)',
    },
  },
  logoutButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    px: 3,
    py: 2,
    backgroundColor: (theme: any) => theme.palette.mode === 'dark'
      ? 'background.shadowBlack'
      : 'background.darkGrey',
    color: 'text.white',
    borderRadius: '12px',
    border: (theme: any) => theme.palette.mode === 'dark'
      ? '1px solid background.black'
      : `1px solid ${theme.palette.border.default}`,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: (theme: any) => theme.typography.fontSizes.body,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: (theme: any) => theme.palette.mode === 'dark'
        ? 'background.shadowBlack'
        : '#3a3a3a',
      borderColor: (theme: any) => theme.palette.mode === 'dark'
        ? 'border.black'
        : theme.palette.border.default,
      transform: 'translateY(-1px)',
      boxShadow: (theme: any) => theme.palette.mode === 'dark'
        ? '0 4px 12px rgba(0,0,0,0.2)'
        : '0 4px 12px rgba(0,0,0,0.08)'
    },
    '&:active': { transform: 'scale(0.98)' },
    '&:focus': {
      outline: 'none', boxShadow: (theme: any) => theme.palette.mode === 'dark'
        ? '0 0 0 2px rgba(0,0,0,0.3)'
        : '0 0 0 2px rgba(0,0,0,0.1)'
    },
  },
  menuItemContainer: {
    mb: 1.5
  },
  divider: {
    my: 1.5,
    borderColor: 'border.divider'
  }
};

// Menu Item Button Component
const MenuItemButton: React.FC<MenuItemButtonProps> = React.memo(({ icon, label, onClick, variant = 'default' }) => (
  <Button
    fullWidth
    onClick={onClick}
    disableElevation
    sx={variant === 'logout' ? styles.logoutButton : styles.menuButton}
    role="menuitem"
  >
    {icon}
    {label}
  </Button>
));

const UserMenu: React.FC<UserMenuProps> = ({ showUserInfo = true }) => {
  const { t } = useTranslation();

  const authContext = useContext(AuthContext)!;
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const version = process.env.REACT_APP_VERSION || 'unknown';

  // Handle OAuth provider linking
  const handleOAuthLinking = useCallback(async (
    provider: string,
    tenantId: number | null,
    authorizationCode: string,
    isTenant: boolean
  ) => {
    if (!tenantId) return;

    try {
      const endpoint = API_ENDPOINTS.users.redeemAuthCode(
        provider,
        isTenant ? String(tenantId) : undefined
      );

      await apiPost(buildApiUrl(endpoint), {
        authorization_code: authorizationCode,
        imap_source: provider === 'gmail' ? 2 : 1,
        smtp_source: provider === 'gmail' ? 2 : 1
      });
      toast.success(t(`Successfully linked ${provider} account`));
    } catch (err) {
      showErrorToast(err, t(`Failed to link ${provider} account: ${err}`));
    }
  }, [t]);

  // Fetch user info and handle OAuth redirects
  const fetchUserInfo = useCallback(async () => {
    // Handle OAuth redirects
    const urlParams = new URLSearchParams(window.location.search);
    const authorizationCode = urlParams.get('code');
    const tenantId = authContext.user?.tenant_id;

    // Handle tenant OAuth (including transEU)
    const tenantProvider = localStorage.getItem('pendingTenantMailProvider');
    if (authorizationCode && tenantProvider && tenantId) {
      await handleOAuthLinking(tenantProvider, tenantId, authorizationCode, true);
    }

    // Handle user OAuth
    const userProvider = localStorage.getItem('pendingUserMailProvider');
    if (authorizationCode && userProvider && tenantId) {
      await handleOAuthLinking(userProvider, tenantId, authorizationCode, false);
    }

    // Clean up only if authorization code was present
    if (authorizationCode && tenantId && (tenantProvider || userProvider)) {
      localStorage.removeItem('pendingTenantMailProvider');
      localStorage.removeItem('pendingUserMailProvider');
      // Clean up URL parameters after OAuth redirect
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [handleOAuthLinking, authContext.user?.tenant_id]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    // Only fetch user info if user is logged in
    if (authContext.isLoggedIn) {
      fetchUserInfo();
    }
  }, [fetchUserInfo, authContext.isLoggedIn, window.location.search]);

  // Keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!menuOpen) return;
      if (e.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  return (
    <Box ref={menuRef} sx={{ position: 'relative', zIndex: 20 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        justifyContent: showUserInfo ? 'flex-start' : 'center',
        width: '100%'
      }}>
        <Button
          onClick={() => {
            setMenuOpen(!menuOpen);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setMenuOpen(!menuOpen);
            }
          }}
          disableElevation
          disableRipple
          sx={{
            p: 0,
            m: 0,
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            transition: 'transform 200ms ease-in-out, color 200ms ease-in-out',
            '&:hover': { transform: 'scale(1.05)', color: 'primary.main' },
            '&:focus': { outline: 'none' },
          }}
          aria-label="User menu"
          aria-expanded={menuOpen}
        >
          <PersonIcon sx={{ fontSize: (theme: any) => theme.typography.fontSizes.heading2, color: 'text.grey' }} />
        </Button>
        {showUserInfo && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography variant="body2" sx={{
              fontWeight: 600,
              fontSize: (theme: any) => theme.typography.fontSizes.body,
              color: 'text.primary',
              lineHeight: 1.2
            }}>
              {authContext.userEmail?.split('@')[0] || 'User'}
            </Typography>
            <Typography variant="caption" sx={{
              fontSize: (theme: any) => theme.typography.fontSizes.small,
              color: 'text.secondary',
              lineHeight: 1.2
            }}>
              {authContext.userEmail || 'user@example.com'}
            </Typography>
          </Box>
        )}
      </Box>

      {menuOpen && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            mb: 2,
            width: 280,
            backgroundColor: 'background.white',
            border: '1px solid border.default',
            borderRadius: '16px',
            p: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            color: 'text.darkGrey',
            zIndex: 1000,
          }}
          role="menu"
          aria-label="User menu"
        >
          {/* User Email Header */}
          <Box sx={{
            px: 2,
            py: 1.5,
            mb: 2,
            backgroundColor: 'background.grey',
            borderRadius: '12px',
            border: '1px solid border.divider'
          }}>
            <Typography sx={{
              fontWeight: 600,
              fontSize: (theme: any) => theme.typography.fontSizes.body,
              color: 'text.darkGrey',
              textAlign: 'center'
            }}>
              {authContext.userEmail || 'user@example.com'}
            </Typography>
          </Box>

          {/* Logout */}
          <Box component="nav" sx={{ mb: 2 }}>
            <MenuItemButton
              icon={<LogoutIcon sx={{ mr: 2, fontSize: 18, color: 'text.white' }} />}
              label={t('Log out')}
              onClick={() => {
                authContext.handleLogout();
                navigate('/login', { replace: true });
              }}
              variant="logout"
            />
          </Box>

          {/* Version Footer */}
          <Divider sx={{ mb: 2, borderColor: 'border.divider' }} />
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: (theme: any) => theme.typography.fontSizes.small,
            color: 'text.grey'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ fontSize: (theme: any) => theme.typography.fontSizes.body, color: 'text.grey' }} />
              <Typography variant="caption" sx={{ color: 'text.grey' }}>{t('Version')}</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'text.grey', fontWeight: 500, fontSize: (theme: any) => theme.typography.fontSizes.small }}>{version}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UserMenu;
